import React from 'react';
import { motion } from 'framer-motion';
import FloatingBackground from '../../Common/FloatingBackground/FloatingBackground';
import ProcessCard, { type ProcessStep } from './ProcessCard';
import './Process.css';

const Process: React.FC = () => {

  const processSteps: ProcessStep[] = [
    {
      number: 1,
      title: "Strategic Discovery & Analysis",
      description: "We conduct comprehensive protocol analysis to understand your study's specific assessment requirements, ensuring every detail is captured and aligned with your research objectives.",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      duration: "Day 1",
      features: ["Protocol Review", "Requirements Analysis", "Study Planning"]
    },
    {
      number: 2,
      title: "Assessment Development, Implementation & Validation",
      description: "We leverage AI to create custom assessments beyond our extensive library, configure your eCOA platform, and validate everything with rigorous testing protocols to ensure regulatory compliance and optimal performance.",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      duration: "Day 2-3",
      features: ["AI Assessment Creation", "Platform Setup", "Quality Assurance", "Compliance Testing"]
    },
    {
      number: 3,
      title: "Training & Go-Live Support",
      description: "We provide comprehensive training programs and multilingual support to ensure your team is fully equipped for successful trial execution.",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      duration: "Day 4",
      features: ["Team Training", "Multilingual Support", "Go-Live Assistance"]
    },
    {
      number: 4,
      title: "Reporting",
      description: "We provide comprehensive reporting capabilities with real-time data analytics, regulatory documentation, and detailed insights to keep you informed throughout your trial.",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      duration: "Immediate",
      features: ["Real-Time Analytics", "Regulatory Documentation", "Data Insights"]
    },
    {
      number: 5,
      title: "Continuous Monitoring & Optimization",
      description: "Our dedicated support team provides ongoing monitoring, performance optimization, and proactive issue resolution throughout your trial lifecycle.",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 20V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 20V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 20V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      duration: "Ongoing",
      features: ["Performance Monitoring", "Data Analytics", "Proactive Support"]
    },
  ];

  return (
    <motion.section 
      className="process modern-process" 
      id="solutions"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <FloatingBackground />
      <div className="container">
        <div className="section-header">
          <h2 className="section-title modern-title">
            Fast-Track Your <span className="gradient-text">Custom Assessments</span> in Under 7 Days
          </h2>
          <p className="section-subtitle">From AI-powered custom assessment creation to comprehensive implementation support, we ensure every study's unique requirements are met</p>
        </div>
        
        <div className="process-flow">
          <div className="process-timeline-enhanced">
            {processSteps.map((step, index) => (
              <div key={step.number}>
                <ProcessCard
                  step={step}
                  index={index}
                  totalSteps={processSteps.length}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Process;
